import { Inject, Injectable, PLATFORM_ID, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import {
  DEFAULT_DOMAIN_KEY,
  DomainMetaConfiguration,
  PageMetaDefinition,
  domainMetaConfig,
} from './meta-config';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  private readonly structuredDataElementId = 'structured-data';
  private readonly trackedExtraNames = new Set<string>();

  constructor(
    private readonly meta: Meta,
    private readonly titleService: Title,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {}

  updateMetaTags(domainName: string) {
    this.applyMeta(domainName);
  }

  updatePageMetaTags(domainName: string, page: string) {
    this.applyMeta(domainName, page);
  }

  addTags(domainName: string) {
    this.applyMeta(domainName);
  }

  public getMetaTags() {
    return this.meta;
  }

  private applyMeta(domainName: string, page?: string) {
    const domainConfig = this.getDomainConfig(domainName);
    const metaDefinition = this.getMetaDefinition(domainConfig, page);

    this.updateTitle(metaDefinition.title);
    this.updateDescription(metaDefinition.description);
    this.updateKeywords(metaDefinition.keywords);
    this.updateRobots(metaDefinition.robots);

    const canonicalUrl = this.buildCanonicalUrl(
      domainConfig.baseUrl,
      metaDefinition.canonicalPath
    );
    this.updateCanonicalLink(canonicalUrl);

    this.updateOpenGraph(metaDefinition, canonicalUrl);
    this.updateTwitter(metaDefinition);
    this.updateExtra(metaDefinition);
    this.updateStructuredData(metaDefinition.structuredData);
  }

  private getDomainConfig(domainName: string): DomainMetaConfiguration {
    return domainMetaConfig[domainName] ?? domainMetaConfig[DEFAULT_DOMAIN_KEY];
  }

  private getMetaDefinition(
    domainConfig: DomainMetaConfiguration,
    page?: string
  ): PageMetaDefinition {
    if (!page) {
      return domainConfig.default;
    }

    const normalizedPage = page.trim().toLowerCase();
    const pageMeta = domainConfig.pages?.[normalizedPage];

    return pageMeta ?? domainConfig.default;
  }

  private updateTitle(title: string) {
    this.titleService.setTitle(title);
    this.meta.updateTag({ name: 'title', content: title });
  }

  private updateDescription(description: string) {
    this.meta.updateTag({ name: 'description', content: description });
  }

  private updateKeywords(keywords?: string | string[]) {
    if (!keywords || (Array.isArray(keywords) && keywords.length === 0)) {
      this.meta.removeTag("name='keywords'");
      return;
    }

    const content = Array.isArray(keywords) ? keywords.join(', ') : keywords;
    this.meta.updateTag({ name: 'keywords', content });
  }

  private updateRobots(robots?: string) {
    if (robots) {
      this.meta.updateTag({ name: 'robots', content: robots });
      return;
    }

    this.meta.removeTag("name='robots'");
  }

  private buildCanonicalUrl(baseUrl: string, canonicalPath?: string) {
    if (!canonicalPath) {
      return baseUrl.replace(/\/$/, '') + '/';
    }

    if (/^https?:\/\//i.test(canonicalPath)) {
      return canonicalPath;
    }

    const trimmedBase = baseUrl.replace(/\/$/, '');
    const normalizedPath = canonicalPath.startsWith('/')
      ? canonicalPath
      : `/${canonicalPath}`;

    return `${trimmedBase}${normalizedPath}`;
  }

  private updateCanonicalLink(canonicalUrl: string) {
    let linkElement = this.document.querySelector(
      "link[rel='canonical']"
    ) as HTMLLinkElement | null;

    if (!linkElement) {
      linkElement = this.document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      this.document.head.appendChild(linkElement);
    }

    linkElement.setAttribute('href', canonicalUrl);
  }

  private updateOpenGraph(
    metaDefinition: PageMetaDefinition,
    canonicalUrl: string
  ) {
    const ogTitle =
      metaDefinition.openGraph?.title ?? metaDefinition.title ?? '';
    const ogDescription =
      metaDefinition.openGraph?.description ?? metaDefinition.description ?? '';
    const ogType = metaDefinition.openGraph?.type ?? 'website';
    const ogSiteName = metaDefinition.openGraph?.siteName;
    const ogImage = metaDefinition.openGraph?.image;

    const tags: Record<string, string> = {
      'og:title': ogTitle,
      'og:description': ogDescription,
      'og:type': ogType,
      'og:url': canonicalUrl,
    };

    if (ogSiteName) {
      tags['og:site_name'] = ogSiteName;
    }

    if (ogImage) {
      tags['og:image'] = ogImage;
    }

    Object.entries(tags).forEach(([property, content]) => {
      if (content) {
        this.meta.updateTag({ property, content });
      }
    });
  }

  private updateTwitter(metaDefinition: PageMetaDefinition) {
    const twitterTitle =
      metaDefinition.twitter?.title ?? metaDefinition.title ?? '';
    const twitterDescription =
      metaDefinition.twitter?.description ?? metaDefinition.description ?? '';
    const twitterCard = metaDefinition.twitter?.card ?? 'summary';
    const twitterImage = metaDefinition.twitter?.image;

    const tags: Record<string, string> = {
      'twitter:card': twitterCard,
      'twitter:title': twitterTitle,
      'twitter:description': twitterDescription,
    };

    if (twitterImage) {
      tags['twitter:image'] = twitterImage;
    }

    Object.entries(tags).forEach(([name, content]) => {
      if (content) {
        this.meta.updateTag({ name, content });
      }
    });
  }

  private updateExtra(metaDefinition: PageMetaDefinition) {
    const extras = metaDefinition.extra ?? {};
    const extrasToRemove = Array.from(this.trackedExtraNames).filter(
      (name) => !(name in extras)
    );

    extrasToRemove.forEach((name) => {
      this.meta.removeTag(`name='${name}'`);
      this.trackedExtraNames.delete(name);
    });

    Object.entries(extras).forEach(([name, content]) => {
      this.meta.updateTag({ name, content });
      this.trackedExtraNames.add(name);
    });
  }

  private updateStructuredData(
    structuredData?: Record<string, unknown> | Array<Record<string, unknown>>
  ) {
    const existing = this.document.getElementById(
      this.structuredDataElementId
    );

    if (!structuredData) {
      if (existing?.parentNode) {
        existing.parentNode.removeChild(existing);
      }
      return;
    }

    const json = Array.isArray(structuredData)
      ? structuredData
      : [structuredData];

    const scriptContent = JSON.stringify(json.length === 1 ? json[0] : json);

    if (existing) {
      existing.textContent = scriptContent;
      return;
    }

    if (!isPlatformBrowser(this.platformId) && !this.document?.head) {
      return;
    }

    const script = this.document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('id', this.structuredDataElementId);
    script.textContent = scriptContent;
    this.document.head.appendChild(script);
  }
}
